import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import StickyBox from 'react-sticky-box';
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIupdateSettings } from '../../../api/Settings';

import AdminPageEditSidebarPageSettings from './Sidebar/PageSettings';
import AdminPageEditSidebarHeaderSettings from './Sidebar/HeaderSettings';
import AdminPageEditSidebarSections from './Sidebar/Sections';
import ProcessingSpinner from '../../ProcessingSpinner';

import { getSetting } from '../../../helpers/Settings';

import ErrorBoundary from '../../ErrorBoundary';

const AdminPageEditSidebar = ({
  saving,
  handleSave,
  ...props
}) => {
  const intl = useIntl();

  let openSidebarSectionsSettings = ['page-settings'];
  const settings = getSetting('page_editor_settings');
  if (settings && settings.open_sidebar_sections) {
    openSidebarSectionsSettings = settings.open_sidebar_sections;
  }

  const [openSidebarSections, setOpenSidebarSections] = useState(openSidebarSectionsSettings);

  const isSectionOpen = sectionId => (
    openSidebarSections.includes(sectionId)
  );

  const switchSection = (sectionId) => {
    if (isSectionOpen(sectionId)) {
      setOpenSidebarSections(openSidebarSections.filter(os => (os !== sectionId)));
    } else {
      setOpenSidebarSections(openSidebarSections.concat([sectionId]));
    }
  };

  useEffect(() => {
    APIupdateSettings({
      page_editor_settings: {
        ...getSetting('page_editor_settings'),
        open_sidebar_sections: openSidebarSections,
      },
    });
  }, [openSidebarSections]);

  return (
    <ErrorBoundary>
      <StickyBox offsetTop={10} offsetBottom={10}>
        <div className="custom-page-edit-sidebar">
          <Card className={classnames({ closed: !isSectionOpen('page-settings') })}>
            <CardHeader onClick={() => { switchSection('page-settings'); }}>
              <span>{intl.formatMessage({ defaultMessage: 'Page settings' })}</span>
              <span>
                {isSectionOpen('page-settings') ? (
                  <i className="fa-solid fa-chevron-up" />
                ) : (
                  <i className="fa-solid fa-chevron-down" />
                )}
              </span>
            </CardHeader>
            <Collapse isOpen={isSectionOpen('page-settings')}>
              <CardBody>
                <AdminPageEditSidebarPageSettings
                  {...props}
                />
              </CardBody>
            </Collapse>
          </Card>
          <Card className={classnames({ closed: !isSectionOpen('header-settings') })}>
            <CardHeader onClick={() => { switchSection('header-settings'); }}>
              <span>{intl.formatMessage({ defaultMessage: 'Header settings' })}</span>
              <span>
                {isSectionOpen('header-settings') ? (
                  <i className="fa-solid fa-chevron-up" />
                ) : (
                  <i className="fa-solid fa-chevron-down" />
                )}
              </span>
            </CardHeader>
            <Collapse isOpen={isSectionOpen('header-settings')}>
              <CardBody>
                <AdminPageEditSidebarHeaderSettings
                  {...props}
                />
              </CardBody>
            </Collapse>
          </Card>
          <Card className={classnames({ closed: !isSectionOpen('sections') })}>
            <CardHeader onClick={() => { switchSection('sections'); }}>
              <span>{intl.formatMessage({ defaultMessage: 'Sections' })}</span>
              <span>
                {isSectionOpen('sections') ? (
                  <i className="fa-solid fa-chevron-up" />
                ) : (
                  <i className="fa-solid fa-chevron-down" />
                )}
              </span>
            </CardHeader>
            <Collapse isOpen={isSectionOpen('sections')}>
              <CardBody>
                <AdminPageEditSidebarSections
                  {...props}
                />
              </CardBody>
            </Collapse>
          </Card>
          <Card className="sidebar-save">
            <CardHeader
              disabled={saving}
              onClick={() => {
                if (!saving) {
                  handleSave();
                }
              }}
            >
              <span>
                {intl.formatMessage({ defaultMessage: 'Save page' })}
              </span>
              <span>
                <ProcessingSpinner show={saving} />
                {!saving && (
                  <i className="fa-solid fa-floppy-disk" />
                )}
              </span>
            </CardHeader>
          </Card>
        </div>
      </StickyBox>
    </ErrorBoundary>
  );
};

AdminPageEditSidebar.propTypes = {
  saving: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
};

export default AdminPageEditSidebar;
